/**
 * SPEC: Obligation Exclusions on Subscriptions — the "Pflichten / Ausschlüsse" panel.
 *
 * Operates on a subscription's (ObligationBinding's) RESOLVED obligation set (never the global
 * catalog). Filter (DIN-276 / Regelung / Pflichttext / Pflichttyp, combinable) → annotated list
 * with included/excluded + stale state → single or bulk toggle with ONE shared comment and a
 * hit-count preview before confirm. Re-include (revoke) is symmetric and audited server-side.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { CheckSquare, ClipboardList, X } from "lucide-react";
import ChecklistPreviewDialog from "./ChecklistPreviewDialog";

import type { ColumnDef } from "@tanstack/react-table";

import { useModalContext } from "../../components/ModalProvider";
import { authenticationAtom } from "../../State";
import { useLegalBindingAPI } from "../../LegalBindingAPI";
import { formatDate } from "../../administration/operator-responsibility/legalDisplay";
import MultiSelect from "./MultiSelect";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const OBLIGATION_TYPES = [
  "inspection",
  "maintenance",
  "documentation",
  "notification",
  "operator",
  "training",
  "conformity",
];

interface Properties {
  binding: any;
  canWrite: boolean;
  onChanged?: () => void;
}

interface Row {
  obligationKey: string;
  version: number;
  type: string;
  regulationId: string;
  regulatedObjectCode: string;
  assetCount?: number;
  regulatedObjectName?: string | null;
  din276Codes?: string[];
  detail: string;
  excluded: boolean;
  exclusion?: { by: string; at: string; comment?: string; stale: boolean };
}

export default function SubscriptionExclusionsPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const bindingId = String(properties.binding._id);
  const fieldId = useId();

  // useLegalBindingAPI returns fresh fn refs each render — keep them in a ref so effects don't loop.
  const api = useLegalBindingAPI(authentication?.customer);
  const apiRef = useRef(api);
  apiRef.current = api;

  const [text, setText] = useState("");
  const [din276, setDin276] = useState("");
  const [types, setTypes] = useState<string[]>([]);
  const [regulationIds, setRegulationIds] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [allRegulations, setAllRegulations] = useState<string[]>([]);
  const queryRequestId = useRef(0);

  const din276Id = `${fieldId}-din276`;
  const textId = `${fieldId}-text`;
  const commentId = `${fieldId}-comment`;

  const filter = useMemo(
    () => ({
      text: text.trim() || undefined,
      din276: din276.trim() ? din276.split(",").map(s => s.trim()).filter(Boolean) : undefined,
      obligationType: types.length ? types : undefined,
      regulationId: regulationIds.length ? regulationIds : undefined,
    }),
    [text, din276, types, regulationIds],
  );

  const filterActive = !!(filter.text || filter.din276 || filter.obligationType || filter.regulationId);

  const refresh = async (f: any, cancelled?: { current: boolean }, rethrow = false) => {
    const requestId = ++queryRequestId.current;
    try {
      setLoading(true);
      const result = await apiRef.current.queryBindingObligations(bindingId, f);
      if (!cancelled?.current && queryRequestId.current === requestId) setRows(result.obligations || []);
    } catch (error) {
      if (!cancelled?.current && queryRequestId.current === requestId) {
        if (!rethrow) pushError(error);
      }
      if (rethrow) throw error;
    } finally {
      if (!cancelled?.current && queryRequestId.current === requestId) setLoading(false);
    }
  };

  // Populate the Regelung dropdown once from the unfiltered set.
  useEffect(() => {
    apiRef.current
      .queryBindingObligations(bindingId, {})
      .then((r: any) =>
        setAllRegulations(
          Array.from(new Set((r.obligations || []).map((o: Row) => o.regulationId).filter(Boolean))) as string[],
        ),
      )
      .catch(() => setAllRegulations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bindingId]);

  // Re-query whenever a filter changes (text debounced).
  useEffect(() => {
    const cancelled = { current: false };
    const handle = setTimeout(() => void refresh(filter, cancelled), 250);
    return () => {
      cancelled.current = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, bindingId]);

  const toggleSelect = (key: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const selectAllMatching = () => setSelected(new Set(rows.map(r => r.obligationKey)));
  const clearSelection = () => {
    setSelected(new Set());
    setComment("");
  };

  const selectedRows = rows.filter(r => selected.has(r.obligationKey));
  const toExclude = selectedRows.filter(r => !r.excluded);
  const toRevoke = selectedRows.filter(r => r.excluded);
  const excludeDinTypes = new Set(toExclude.map(r => r.regulatedObjectCode)).size;

  const apply = async (action: "exclude" | "revoke") => {
    try {
      setLoading(true);
      const keys = (action === "exclude" ? toExclude : toRevoke).map(r => r.obligationKey);
      if (!keys.length) return;
      if (action === "exclude") {
        await apiRef.current.excludeObligations(bindingId, keys, comment.trim() || undefined);
      } else {
        await apiRef.current.revokeExclusions(bindingId, keys);
      }
      await refresh(filter, undefined, true);
      setComment("");
      clearSelection();
      properties.onChanged?.();
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
  };

  // The checklist a duty would produce, in a dialog.
  const [checklistKey, setChecklistKey] = useState<string | null>(null);

  const columns: ColumnDef<Row>[] = [
    {
      id: "select",
      header: () => <span className="sr-only">{t("global.select")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <Checkbox
          checked={selected.has(row.original.obligationKey)}
          aria-label={`${t("global.select")}: ${row.original.obligationKey}`}
          onCheckedChange={() => toggleSelect(row.original.obligationKey)}
        />
      ),
    },
    {
      id: "assetCount",
      accessorFn: (row: Row) => row.assetCount ?? 0,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.exclusions.assets")}</span>,
      enableSorting: false,
      // How many of the subscription's assets the duty applies to — what excluding it saves.
      cell: ({ row }) => <span className="text-sm tabular-nums">{(row.original.assetCount ?? 0).toLocaleString("de-DE")}</span>,
    },
    {
      id: "regulation",
      accessorFn: (row: Row) => row.regulationId,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.exclusions.regulation")}</span>,
      enableSorting: false,
      cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original.regulationId}</span>,
    },
    {
      id: "din276",
      accessorFn: (row: Row) => row.regulatedObjectName ?? row.regulatedObjectCode,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.exclusions.object")}</span>,
      enableSorting: false,
      // The regulated object by name, its DIN 276 nodes below — the key alone („ventilationSystem“) says little.
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="break-words text-sm">{row.original.regulatedObjectName ?? row.original.regulatedObjectCode}</span>
          {row.original.din276Codes?.length ? (
            <span className="break-words text-xs text-muted-foreground">DIN 276 {row.original.din276Codes.join(", ")}</span>
          ) : null}
        </div>
      ),
    },
    {
      id: "checklist",
      header: () => <span className="sr-only">{t("operatorObligationsPanel.exclusions.showChecklist")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          aria-label={`${t("operatorObligationsPanel.exclusions.showChecklist")}: ${row.original.obligationKey}`}
          title={t("operatorObligationsPanel.exclusions.showChecklist")}
          data-testid="exclusion-show-checklist"
          onClick={() => setChecklistKey(row.original.obligationKey)}
        >
          <ClipboardList aria-hidden className="h-4 w-4" />
        </Button>
      ),
    },
    {
      id: "type",
      accessorFn: (row: Row) => row.type,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.exclusions.type")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="min-w-0 break-words text-sm">
          {t(`operatorObligationsPanel.exclusions.typeValue.${row.original.type}`, row.original.type)}
        </span>
      ),
    },
    {
      id: "detail",
      accessorFn: (row: Row) => row.detail,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.exclusions.detail")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[360px] break-words text-sm">{row.original.detail || "—"}</span>
      ),
    },
    {
      id: "state",
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.exclusions.state")}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const item = row.original;
        if (!item.excluded) {
          return <span className="text-sm text-success">{t("operatorObligationsPanel.exclusions.included")}</span>;
        }
        return (
          <div className="flex min-w-0 flex-col">
            <span
              className="text-sm text-warning"
              title={`${item.exclusion?.by} · ${formatDate(item.exclusion?.at)}${item.exclusion?.comment ? " · " + item.exclusion.comment : ""}`}
            >
              {t("operatorObligationsPanel.exclusions.excluded")}
            </span>
            {item.exclusion?.stale ? (
              <span className="text-xs text-destructive">
                {t("operatorObligationsPanel.exclusions.stale")}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">{t("operatorObligationsPanel.exclusions.exclude")}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const item = row.original;
        if (!properties.canWrite) return null;
        return item.excluded ? (
          <Button
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => apiRef.current.revokeExclusions(bindingId, [item.obligationKey]).then(() => { refresh(filter); properties.onChanged?.(); }).catch(pushError)}
          >
            {t("operatorObligationsPanel.exclusions.include")}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => apiRef.current.excludeObligations(bindingId, [item.obligationKey], comment.trim() || undefined).then(() => { refresh(filter); properties.onChanged?.(); }).catch(pushError)}
          >
            {t("operatorObligationsPanel.exclusions.exclude")}
          </Button>
        );
      },
    },
  ];

  const typeOptions = OBLIGATION_TYPES.map(ty => ({
    key: ty,
    text: t(`operatorObligationsPanel.exclusions.typeValue.${ty}`, ty),
  }));
  const regulationOptions = allRegulations.map(r => ({ key: r, text: r }));

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex w-[160px] max-w-full flex-col gap-1.5">
          <Label htmlFor={din276Id}>{t("operatorObligationsPanel.exclusions.din276")}</Label>
          <Input
            id={din276Id}
            placeholder={t("operatorObligationsPanel.exclusions.din276Placeholder")}
            value={din276}
            onChange={(event) => setDin276(event.target.value)}
          />
        </div>
        <MultiSelect
          label={t("operatorObligationsPanel.exclusions.regulation")}
          placeholder={t("global.pleaseSelect")}
          emptyLabel={t("global.noInformationAvailable")}
          options={regulationOptions}
          selectedKeys={regulationIds}
          onChange={setRegulationIds}
        />
        <div className="flex w-[240px] max-w-full flex-col gap-1.5">
          <Label htmlFor={textId}>{t("operatorObligationsPanel.exclusions.text")}</Label>
          <Input id={textId} value={text} onChange={(event) => setText(event.target.value)} />
        </div>
        <MultiSelect
          label={t("operatorObligationsPanel.exclusions.type")}
          placeholder={t("global.pleaseSelect")}
          emptyLabel={t("global.noInformationAvailable")}
          options={typeOptions}
          selectedKeys={types}
          onChange={setTypes}
        />
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-4">
        <Button variant="ghost" size="sm" className="gap-2" onClick={selectAllMatching}>
          <CheckSquare aria-hidden className="h-4 w-4" />
          {t("operatorObligationsPanel.exclusions.selectAll", { count: rows.length })}
        </Button>
        {selected.size > 0 ? (
          <Button variant="ghost" size="sm" className="gap-2" onClick={clearSelection}>
            <X aria-hidden className="h-4 w-4" />
            {t("operatorObligationsPanel.exclusions.clear")}
          </Button>
        ) : null}
        {properties.canWrite && selected.size > 0 ? (
          <div className="flex w-[280px] max-w-full flex-col gap-1.5">
            <Label htmlFor={commentId} className="sr-only">
              {t("operatorObligationsPanel.exclusions.commentPlaceholder")}
            </Label>
            <Input
              id={commentId}
              placeholder={t("operatorObligationsPanel.exclusions.commentPlaceholder")}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </div>
        ) : null}
        {properties.canWrite && toExclude.length > 0 ? (
          <Button onClick={() => apply("exclude")} disabled={loading}>
            {t("operatorObligationsPanel.exclusions.previewExclude", {
              count: toExclude.length,
              dinTypes: excludeDinTypes,
            })}
          </Button>
        ) : null}
        {properties.canWrite && toRevoke.length > 0 ? (
          <Button variant="outline" onClick={() => apply("revoke")} disabled={loading}>
            {t("operatorObligationsPanel.exclusions.previewRevoke", { count: toRevoke.length })}
          </Button>
        ) : null}
      </div>

      <div className="min-w-0 overflow-x-auto">
        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          previousAriaLabel={t("global.back")}
          nextAriaLabel={t("global.next")}
          emptyMessage={
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <div className="text-sm font-semibold text-foreground">
                {filterActive
                  ? t("operatorObligationsPanel.exclusions.noMatches")
                  : t("operatorObligationsPanel.exclusions.noObligations")}
              </div>
              <div className="max-w-prose text-sm text-muted-foreground">
                {filterActive
                  ? t("operatorObligationsPanel.exclusions.noMatchesHint")
                  : t("operatorObligationsPanel.exclusions.noObligationsHint")}
              </div>
              {filterActive ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDin276("");
                    setText("");
                    setTypes([]);
                    setRegulationIds([]);
                  }}
                >
                  {t("operatorObligationsPanel.exclusions.resetFilter")}
                </Button>
              ) : null}
            </div>
          }
        />
      </div>
      <ChecklistPreviewDialog obligationKey={checklistKey} bindingId={bindingId} onClose={() => setChecklistKey(null)} />
    </div>
  );
}
